// FILTVDigitalTwin.jsx
// Think5Whys — FILTV (FindItLocal TV) — "Digital Twin" live shopping
//
// Concept: a State-of-the-art Think5Whys DigitalTwin Device sits at a local
// shop (fish counter, tandoor, kirana, sweet shop), mounted or handheld,
// facing the product + the shopkeeper. A customer browses live "channels"
// (FILTV), opens a shop's Digital Twin, watches the live feed + sensor
// context (freshness/temperature), talks to the shopkeeper, and places a
// Cash-on-Delivery order. Repeated live engagement earns Streak Points.
// Front-end only — video, chat and IoT readings are simulated so the flow
// can be demoed before the hardware prototype exists.
//
// Icons: Font Awesome (loaded from cdnjs at runtime, no npm import needed)
// Styling: Tailwind core utility classes only

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

/* ---------------------------------- data ---------------------------------- */

const CATEGORY_META = {
  fish: { label: "Fish Counter", icon: "fa-fish", unit: "ice-box temp" },
  tandoor: { label: "Tandoor & Bakes", icon: "fa-fire", unit: "oven temp" },
  kirana: { label: "Kirana Store", icon: "fa-store", unit: "shelf ambient" },
  sweets: { label: "Sweet Shop", icon: "fa-cookie-bite", unit: "counter temp" },
};

const INITIAL_SHOPS = [
  {
    id: "shp_rohu",
    name: "Ganga Fish Corner",
    owner: "Bimal Das",
    category: "fish",
    tagline: "Daily catch, iced within the hour",
    streaming: true,
    products: [
      { id: "p1", name: "Rohu (per kg)", price: 260, unit: "kg" },
      { id: "p2", name: "Prawns (per kg)", price: 480, unit: "kg" },
    ],
  },
  {
    id: "shp_tandoor",
    name: "Bhatti Wale Tandoor",
    owner: "Gurmeet Singh",
    category: "tandoor",
    tagline: "Live tawa, fresh off the fire",
    streaming: true,
    products: [
      { id: "p3", name: "Tandoori Roti (pack of 5)", price: 60, unit: "pack" },
      { id: "p4", name: "Stuffed Kulcha (pair)", price: 50, unit: "pair" },
    ],
  },
  {
    id: "shp_kirana",
    name: "Shree Ram Kirana",
    owner: "Meena Verma",
    category: "kirana",
    tagline: "Local staples, verified stock in view",
    streaming: false,
    products: [
      { id: "p5", name: "Toor Dal (1kg)", price: 145, unit: "kg" },
      { id: "p6", name: "Mustard Oil (1L)", price: 165, unit: "L" },
    ],
  },
  {
    id: "shp_sweets",
    name: "Anand Sweets",
    owner: "Rakesh Anand",
    category: "sweets",
    tagline: "Watch it being made, watch it packed",
    streaming: true,
    products: [
      { id: "p7", name: "Milk Barfi (500g)", price: 220, unit: "box" },
      { id: "p8", name: "Fresh Rasgulla (12 pc)", price: 180, unit: "box" },
    ],
  },
];

const SHOPKEEPER_REPLIES = [
  "Ji bolo, kya dekhna chahenge?",
  "Bilkul taaza hai, abhi camera ke saamne hi hai.",
  "Haan, aapke liye pack kar deta hoon.",
  "5 minute mein ready ho jayega, COD chalega.",
  "Aap zoom karke dekh lijiye, seedha counter se dikha raha hoon.",
];

function genSensorPoint(category) {
  const base = { fish: 4, tandoor: 260, kirana: 26, sweets: 22 }[category];
  const spread = { fish: 2, tandoor: 15, kirana: 3, sweets: 3 }[category];
  return {
    t: new Date().toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }),
    temp: +(base + (Math.random() - 0.5) * spread).toFixed(1),
    humidity: +(45 + Math.random() * 20).toFixed(0),
  };
}

function currency(n) {
  return `₹${n.toLocaleString("en-IN")}`;
}

/* -------------------------------- FA loader -------------------------------- */

function useFontAwesome() {
  useEffect(() => {
    const id = "fa6-cdn-link";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css";
    document.head.appendChild(link);
  }, []);
}

/* --------------------------------- component -------------------------------- */

export default function FILTVDigitalTwin() {
  useFontAwesome();

  const [shops, setShops] = useState(INITIAL_SHOPS);
  const [selectedId, setSelectedId] = useState(INITIAL_SHOPS[0].id);
  const [sensorHistory, setSensorHistory] = useState(() =>
    Object.fromEntries(
      INITIAL_SHOPS.map((s) => [s.id, [genSensorPoint(s.category)]]),
    ),
  );
  const [gallery, setGallery] = useState(() =>
    Object.fromEntries(INITIAL_SHOPS.map((s) => [s.id, []])),
  );
  const [chatOpen, setChatOpen] = useState(false);
  const [chatLog, setChatLog] = useState(() =>
    Object.fromEntries(INITIAL_SHOPS.map((s) => [s.id, []])),
  );
  const [chatDraft, setChatDraft] = useState("");
  const [cart, setCart] = useState([]);
  const [orders, setOrders] = useState([]);
  const [streak, setStreak] = useState({ points: 40, viewedToday: new Set() });
  const [toast, setToast] = useState(null);
  const chatEndRef = useRef(null);
  const toastTimer = useRef(null);

  const selectedShop = shops.find((s) => s.id === selectedId);

  useEffect(() => {
    const iv = setInterval(() => {
      setSensorHistory((prev) => {
        const next = { ...prev };
        shops.forEach((s) => {
          if (!s.streaming) return;
          next[s.id] = [...(prev[s.id] || []).slice(-14), genSensorPoint(s.category)];
        });
        return next;
      });
    }, 2500);
    return () => clearInterval(iv);
  }, [shops]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatLog, chatOpen, selectedId]);

  function flashToast(text) {
    setToast(text);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 2200);
  }

  function openShop(id) {
    setSelectedId(id);
    setChatOpen(false);
    setStreak((s) => {
      if (s.viewedToday.has(id)) return s;
      const viewedToday = new Set(s.viewedToday);
      viewedToday.add(id);
      flashToast("+5 streak points for watching a live shop today");
      return { points: s.points + 5, viewedToday };
    });
  }

  function toggleStreaming(id) {
    setShops((prev) =>
      prev.map((s) => (s.id === id ? { ...s, streaming: !s.streaming } : s)),
    );
  }

  function captureImage(id) {
    const shop = shops.find((s) => s.id === id);
    const shot = {
      id: "img_" + Math.random().toString(36).slice(2, 8),
      ts: new Date(),
      note: `${CATEGORY_META[shop.category].label} — counter view`,
    };
    setGallery((g) => ({ ...g, [id]: [shot, ...(g[id] || [])].slice(0, 6) }));
    flashToast("Snapshot saved as twin evidence");
  }

  function sendChat() {
    if (!chatDraft.trim()) return;
    const id = selectedId;
    const mine = { from: "me", text: chatDraft.trim(), ts: Date.now() };
    setChatLog((c) => ({ ...c, [id]: [...(c[id] || []), mine] }));
    setChatDraft("");
    window.setTimeout(() => {
      const reply = {
        from: "shopkeeper",
        text: SHOPKEEPER_REPLIES[Math.floor(Math.random() * SHOPKEEPER_REPLIES.length)],
        ts: Date.now(),
      };
      setChatLog((c) => ({ ...c, [id]: [...(c[id] || []), reply] }));
    }, 900);
  }

  function addToCart(shopId, productId) {
    setCart((c) => {
      const existing = c.find((i) => i.shopId === shopId && i.productId === productId);
      if (existing) {
        return c.map((i) => (i === existing ? { ...i, qty: i.qty + 1 } : i));
      }
      return [...c, { shopId, productId, qty: 1 }];
    });
    flashToast("Added to cart");
  }

  function removeFromCart(shopId, productId) {
    setCart((c) => c.filter((i) => !(i.shopId === shopId && i.productId === productId)));
  }

  const cartTotal = useMemo(() => {
    return cart.reduce((sum, item) => {
      const shop = shops.find((s) => s.id === item.shopId);
      const product = shop?.products.find((p) => p.id === item.productId);
      return sum + (product ? product.price * item.qty : 0);
    }, 0);
  }, [cart, shops]);

  function placeOrder() {
    if (cart.length === 0) return;
    const order = {
      id: "ORD" + Math.random().toString(36).slice(2, 7).toUpperCase(),
      items: cart,
      total: cartTotal,
      ts: new Date(),
      status: "Confirmed · Cash on Delivery",
    };
    setOrders((o) => [order, ...o]);
    setCart([]);
    setStreak((s) => ({ ...s, points: s.points + 15 }));
    flashToast("Order placed · +15 streak points");
  }

  const liveCount = shops.filter((s) => s.streaming).length;
  const latestReading = sensorHistory[selectedId]?.slice(-1)[0];

  return (
    <div className="min-h-screen bg-stone-950 text-stone-50 font-sans">
      {/* header */}
      <header className="border-b border-stone-800 px-5 py-3.5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-baseline gap-2.5">
          <span className="text-3xl font-extrabold text-amber-500 leading-none">FILTV</span>
          <span className="text-xs text-stone-400">
            FindItLocal TV · a Think5Whys module · powered by State-of-the-art DigitalTwin Devices
          </span>
        </div>
        <div className="flex items-center gap-3.5">
          <div className="flex items-center gap-1.5 text-sm text-stone-400">
            <i className="fa-solid fa-tower-broadcast text-emerald-600 text-sm" />
            <span>{liveCount} shops live now</span>
          </div>
          <div className="font-mono flex items-center gap-1.5 bg-stone-900 border border-stone-800 rounded-full px-3 py-1.5 text-sm">
            <i className="fa-solid fa-signal text-amber-500 text-sm" />
            <span>{streak.points} streak pts</span>
          </div>
        </div>
      </header>

      {/* channel rail */}
      <section className="px-5 pt-4 pb-1">
        <div className="text-xs uppercase tracking-wider text-stone-400 mb-2.5">
          Live Shopping Channels
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2">
          {shops.map((s) => {
            const active = s.id === selectedId;
            return (
              <button
                key={s.id}
                onClick={() => openShop(s.id)}
                className={`flex-none w-48 text-left rounded-xl p-3 border ${
                  active ? "bg-amber-950 border-amber-500" : "bg-stone-900 border-stone-800"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5 text-stone-50">
                    <i className={`fa-solid ${CATEGORY_META[s.category].icon} text-amber-500 text-sm`} />
                    <span className="text-[11px] text-stone-400">{CATEGORY_META[s.category].label}</span>
                  </div>
                  {s.streaming ? (
                    <span className="flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse inline-block" />
                      LIVE
                    </span>
                  ) : (
                    <span className="text-[10px] text-stone-500">OFFLINE</span>
                  )}
                </div>
                <div className="text-lg font-bold leading-tight mb-0.5">{s.name}</div>
                <div className="text-xs text-stone-400">{s.tagline}</div>
              </button>
            );
          })}
        </div>
      </section>

      {/* main stage */}
      {selectedShop && (
        <section className="px-5 pb-8 pt-3 grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-4">
          {/* left: live view */}
          <div>
            <div className="relative bg-black rounded-2xl aspect-video flex items-center justify-center border border-stone-800 overflow-hidden">
              {selectedShop.streaming ? (
                <div className="text-center text-stone-500">
                  <i className="fa-solid fa-camera text-3xl mb-2 opacity-60 block" />
                  <div className="font-mono text-xs">Device feed · {selectedShop.name}</div>
                </div>
              ) : (
                <div className="text-center text-stone-500">
                  <div className="font-mono text-xs">Device offline</div>
                </div>
              )}
              {selectedShop.streaming && (
                <span className="absolute top-2.5 left-2.5 flex items-center gap-1.5 bg-red-600/90 px-2 py-0.5 rounded-full text-[11px] font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse inline-block" /> LIVE
                </span>
              )}
              <span className="absolute top-2.5 right-2.5 flex items-center gap-1.5 bg-stone-900/90 border border-amber-500 px-2 py-0.5 rounded-full text-[10px] text-amber-500 font-medium">
                <i className="fa-solid fa-microchip text-[10px]" />
                State-of-the-art Think5Whys DigitalTwin Device
              </span>
              <div className="absolute bottom-2.5 left-2.5 right-2.5 flex justify-between items-end">
                <div className="font-mono text-[11px] text-stone-300">
                  {selectedShop.owner} · {CATEGORY_META[selectedShop.category].unit}
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-2.5 flex-wrap">
              <button
                onClick={() => toggleStreaming(selectedShop.id)}
                className={`flex items-center gap-1.5 border border-stone-800 rounded-lg px-3.5 py-2 text-sm ${
                  selectedShop.streaming ? "bg-red-950 text-stone-50" : "bg-emerald-950 text-stone-50"
                }`}
              >
                <i className="fa-solid fa-tower-broadcast text-sm" />
                {selectedShop.streaming ? "Stop device feed" : "Start device feed"}
              </button>
              <button
                onClick={() => captureImage(selectedShop.id)}
                className="flex items-center gap-1.5 bg-stone-900 text-stone-50 border border-stone-800 rounded-lg px-3.5 py-2 text-sm"
              >
                <i className="fa-solid fa-camera text-sm" />
                Capture twin evidence
              </button>
              <button
                onClick={() => setChatOpen(true)}
                className="flex items-center gap-1.5 bg-amber-500 text-stone-950 border border-amber-500 rounded-lg px-3.5 py-2 text-sm font-semibold"
              >
                <i className="fa-solid fa-comment-dots text-sm" />
                Talk to {selectedShop.owner.split(" ")[0]}
              </button>
            </div>

            {/* sensor context */}
            <div className="mt-4 bg-stone-900 border border-stone-800 rounded-xl p-3.5">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider text-stone-400">Freshness context</div>
                <div className="flex gap-3 text-xs font-mono">
                  <span className="flex items-center gap-1 text-red-400">
                    <i className="fa-solid fa-temperature-half text-[11px]" />
                    {latestReading?.temp}°C
                  </span>
                  <span className="flex items-center gap-1 text-sky-400">
                    <i className="fa-solid fa-droplet text-[11px]" />
                    {latestReading?.humidity}%
                  </span>
                </div>
              </div>
              <div className="w-full h-36">
                <ResponsiveContainer>
                  <LineChart data={sensorHistory[selectedShop.id] || []}>
                    <CartesianGrid stroke="#292524" strokeDasharray="3 3" />
                    <XAxis dataKey="t" tick={{ fontSize: 10, fill: "#78716c" }} minTickGap={30} />
                    <YAxis tick={{ fontSize: 10, fill: "#78716c" }} width={28} />
                    <Tooltip contentStyle={{ background: "#1c1917", border: "1px solid #292524", fontSize: 12 }} labelStyle={{ color: "#a8a29e" }} />
                    <Line type="monotone" dataKey="temp" stroke="#f87171" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="humidity" stroke="#38bdf8" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* gallery */}
            {gallery[selectedShop.id]?.length > 0 && (
              <div className="mt-4">
                <div className="text-xs uppercase tracking-wider text-stone-400 mb-2">Twin evidence</div>
                <div className="flex gap-2.5 overflow-x-auto">
                  {gallery[selectedShop.id].map((g) => (
                    <div key={g.id} className="flex-none w-32 bg-stone-900 border border-stone-800 rounded-lg p-2">
                      <div className="w-full h-16 bg-black rounded-md mb-1.5 flex items-center justify-center">
                        <i className="fa-solid fa-camera text-stone-600 text-sm" />
                      </div>
                      <div className="text-[10px] text-stone-400">{g.ts.toLocaleTimeString("en-IN")}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* right: products + cart + orders */}
          <div className="flex flex-col gap-3.5">
            <div className="bg-stone-900 border border-stone-800 rounded-xl p-3.5">
              <div className="text-xs uppercase tracking-wider text-stone-400 mb-2.5">
                {selectedShop.name} · Order (COD)
              </div>
              {selectedShop.products.map((p) => (
                <div key={p.id} className="flex items-center justify-between py-2 border-b border-stone-800 last:border-b-0">
                  <div>
                    <div className="text-sm">{p.name}</div>
                    <div className="font-mono text-xs text-stone-400">{currency(p.price)} / {p.unit}</div>
                  </div>
                  <button
                    onClick={() => addToCart(selectedShop.id, p.id)}
                    className="bg-amber-950 border border-amber-500 text-amber-500 rounded-md w-fit px-2.5 py-1.5 text-xs"
                  >
                    Add
                  </button>
                </div>
              ))}
            </div>

            <div className="bg-stone-900 border border-stone-800 rounded-xl p-3.5">
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-stone-400 mb-2.5">
                <i className="fa-solid fa-bag-shopping text-[11px]" /> Cart
              </div>
              {cart.length === 0 ? (
                <div className="text-sm text-stone-500">Nothing added yet — pick something while it's live.</div>
              ) : (
                <>
                  {cart.map((item) => {
                    const shop = shops.find((s) => s.id === item.shopId);
                    const product = shop.products.find((p) => p.id === item.productId);
                    return (
                      <div key={item.shopId + item.productId} className="flex justify-between items-center text-sm py-1">
                        <span>
                          {product.name} × {item.qty} <span className="text-stone-500">({shop.name})</span>
                        </span>
                        <button
                          onClick={() => removeFromCart(item.shopId, item.productId)}
                          className="text-stone-400 px-1"
                          aria-label={`Remove ${product.name}`}
                        >
                          <i className="fa-solid fa-xmark text-sm" />
                        </button>
                      </div>
                    );
                  })}
                  <div className="flex justify-between mt-2.5 pt-2.5 border-t border-stone-800 text-sm">
                    <span>Total (COD)</span>
                    <span className="font-mono">{currency(cartTotal)}</span>
                  </div>
                  <button
                    onClick={placeOrder}
                    className="w-full mt-2.5 bg-amber-500 text-stone-950 rounded-lg py-2.5 text-sm font-bold"
                  >
                    Place order · Pay on delivery
                  </button>
                </>
              )}
            </div>

            {orders.length > 0 && (
              <div className="bg-stone-900 border border-stone-800 rounded-xl p-3.5">
                <div className="text-xs uppercase tracking-wider text-stone-400 mb-2.5">Recent orders</div>
                {orders.slice(0, 3).map((o) => (
                  <div key={o.id} className="flex items-center gap-2 text-[13px] py-1.5 text-stone-300">
                    <i className="fa-solid fa-check text-emerald-600 text-xs" />
                    <span className="font-mono">{o.id}</span>
                    <i className="fa-solid fa-chevron-right text-stone-500 text-[10px]" />
                    <span>{currency(o.total)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* chat overlay */}
      {chatOpen && selectedShop && (
        <div
          className="fixed inset-0 bg-black/55 flex justify-end z-50"
          onClick={() => setChatOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm h-full bg-stone-950 border-l border-stone-800 flex flex-col"
          >
            <div className="p-3.5 border-b border-stone-800 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold">{selectedShop.owner}</div>
                <div className="text-[11px] text-stone-400">{selectedShop.name} · live now</div>
              </div>
              <button onClick={() => setChatOpen(false)} className="text-stone-400" aria-label="Close chat">
                <i className="fa-solid fa-xmark text-lg" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3.5 flex flex-col gap-2">
              {(chatLog[selectedShop.id] || []).length === 0 && (
                <div className="text-xs text-stone-500">
                  Say hello — the shopkeeper can see you're watching live.
                </div>
              )}
              {(chatLog[selectedShop.id] || []).map((m, i) => (
                <div
                  key={i}
                  className={`max-w-[85%] rounded-lg px-2.5 py-1.5 text-sm ${
                    m.from === "me"
                      ? "self-end bg-amber-500 text-stone-950"
                      : "self-start bg-stone-900 text-stone-50"
                  }`}
                >
                  {m.text}
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            <div className="p-2.5 border-t border-stone-800 flex gap-2">
              <input
                value={chatDraft}
                onChange={(e) => setChatDraft(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendChat()}
                placeholder="Type a message…"
                className="flex-1 bg-stone-900 border border-stone-800 rounded-lg px-2.5 py-2 text-stone-50 text-sm"
              />
              <button
                onClick={sendChat}
                className="bg-amber-500 rounded-lg px-3 text-stone-950"
                aria-label="Send message"
              >
                <i className="fa-solid fa-paper-plane text-sm" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* toast */}
      {toast && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 bg-stone-900 border border-amber-500 text-stone-50 px-4 py-2 rounded-full text-xs z-[60]">
          {toast}
        </div>
      )}
    </div>
  );
}